import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { JSONSchema7 } from 'json-schema';
import { useTheme } from '@mui/material';

// Function to generate columns from JSON schema
const generateColumnsFromSchema = (
  schema: JSONSchema7,
  editableColumns: string[] = [],
  hasIdInData: boolean
): GridColDef[] => {
  const columns: GridColDef[] = [];

  // Check if schema already has an id property
  const hasIdInSchema = schema.properties && typeof schema.properties === 'object' && 'id' in schema.properties;

  // Add ID column if it's not in the schema or if we're adding it to the rows
  if (!hasIdInSchema || hasIdInData) {
    columns.push({ field: 'id', headerName: 'ID', width: 90 });
  }

  // Process schema properties
  if (schema.properties && typeof schema.properties === 'object') {
    Object.entries(schema.properties).forEach(([key, prop]) => {
      if (key !== 'id') { // Skip ID as we've already added it
        const isEditable = editableColumns?.includes(key);

        const column: GridColDef = {
          field: key,
          headerName: (prop as any).title || key.charAt(0).toUpperCase() + key.slice(1),
          width: 150,
          editable: isEditable,
        };

        // Set column type based on schema type
        if ((prop as any).type === 'number' || (prop as any).type === 'integer') {
          column.type = 'number';
          column.width = 110;
        } else if ((prop as any).type === 'boolean') {
          column.type = 'boolean';
          column.width = 120;
        } else if ((prop as any).format === 'date-time' || (prop as any).format === 'date') {
          column.type = 'dateTime';
          column.width = 180;
        }

        columns.push(column);
      }
    });
  }

  return columns;
};

export function DatasetView(props: {
  dataSchema: JSONSchema7,
  rows: object[],
  editableColumns?: string[],
  checkboxSelection?: boolean,
}) {
  const theme = useTheme();
  // Check if rows have ID field
  const hasIdField = React.useMemo(() =>
    props.rows.length === 0 || props.rows.some(row => 'id' in row),
    [props.rows]
  );

  // Process rows to ensure they have id column
  const processedRows = React.useMemo(() => {
    if (hasIdField) {
      return props.rows;
    } else {
      // If id field is missing, add it based on row index
      return props.rows.map((row, index) => ({
        id: index + 1,
        ...row
      }));
    }
  }, [props.rows, hasIdField]);

  // Generate columns from schema, passing info about whether we're adding IDs
  const columns = React.useMemo(() =>
    generateColumnsFromSchema(props.dataSchema, props.editableColumns, !hasIdField),
    [props.dataSchema, props.editableColumns, hasIdField]
  );

  return (
    <Box sx={{
      p: 2,
      width: '100%',
      height: '100%',
      backgroundColor: theme.palette.background.paper,
      borderRadius: 2,
      boxShadow: 1
    }}
    >
      <DataGrid
        autoPageSize
        rows={processedRows}
        columns={columns}
        // paginationMode='server'
        // rowsPerPageOptions={[props.pageSize || 10]}
        checkboxSelection={props.checkboxSelection}
      />
    </Box>
  );
}

export default DatasetView;
